import React from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import Text from '../../components/basic/Text';
import SupportHero from '../../components/customer-service/SupportHero';
import Section from '../../components/basic/layouts/Section';

import Link from '../../components/util/link';
import Layout from '../../components/basic/layouts/NoMenu';
import Button from '../../components/basic/Button';
import theme from '../../styles/theme';
import constants from '../../constants';

import HeroImage from '../../images/help/SubmitterHelp.svg';

const {
	colors,
	breakPoints: { screenQuery },
} = theme;

const { helpPage } = constants;

const GridWrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr 2fr;
	grid-template-rows: 1fr;
	grid-gap: 40px;
	align-items: start;
	${screenQuery.medium} {
		grid-template-columns: 1fr;
	}
`;
const Disclaimer = styled.div`
	border-left: 10px solid ${colors.tertiary.main};
	padding-left: 25px;
	align-self: center;
`;
const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: 40px;
	grid-auto-rows: 1fr;
	.span2 {
		grid-column-start: 2;
		grid-column-end: 4;
	}
	.span-first-2 {
		grid-column-start: 1;
		grid-column-end: 3;
	}
	${screenQuery.medium} {
		grid-template-columns: 1fr;
		grid-auto-rows: auto;
		.center-medium {
			text-align: center;
		}
		.note {
			max-width: 400px;
			margin: 0 auto;
		}
		.span2,
		.span-first-2 {
			grid-column-start: auto;
			grid-column-end: auto;
		}
	}
`;
const GridLink = styled(Link)`
	display: block;
	height: 100%;
	width: 100%;
	max-width: 400px;
	margin: 0 auto;
	font-family: 'ProximaNova-Semibold';
	box-shadow: ${theme.boxShadow};
	background-color: ${colors.grayscale.white};
	padding: 15px;
	display: flex;
	align-items: center;
	font-size: 1.75em;
	color: ${colors.grayscale.main};
	:hover {
		cursor: pointer;
		box-shadow: ${theme.hoverBoxShadow};
		color: ${colors.grayscale.main};
	}
`;

const helpTopics = [
	{
		text: 'What is the status of my submission?',
		link: 'https://submittable.help/en/articles/907292-how-do-i-check-the-status-of-my-application',
	},
	{
		text: 'How do I create an account?',
		link: 'https://submittable.help/en/articles/923475-why-do-i-need-a-submittable-account-to-make-a-submission',
	},
	{
		text: 'How do I submit?',
		link: 'https://submittable.help/en/articles/904856-how-do-i-submit',
	},
	{
		text: 'How can I contact an organization to which I submitted?',
		link: 'https://submittable.help/en/articles/2576225-how-can-i-communicate-with-an-organization-to-which-i-ve-made-a-submission',
	},
	{
		text: 'How can I view a submission that I’ve made?',
		link: 'https://submittable.help/en/articles/904859-how-can-i-view-a-submission-that-i-ve-made',
	},

	{
		text: 'How can I request an edit to a submission?',
		link: 'https://submittable.help/en/articles/904890-how-can-i-request-an-edit-on-my-submission',
	},
];

const Page = ({ location }) => {
	return (
		<Layout
			pageTitle={`Submittable Help - For Submitters`}
			description={`Support and resources for individuals who submit their work to organizations via the Submittable platform`}
			location={location}
			noIndex
			lightHeader
			headerLogoLink="https://manager.submittable.com/"
			footerLogoLink="https://manager.submittable.com/"
		>
			<SupportHero
				headerText="We're here to help"
				subHeaderText="Find how-to instructions and guides and get answers to frequently asked questions."
				heroImage={HeroImage}
				topBottomSpace="60px"
			/>
			<Section backgroundColor={colors.grayscale.stone}>
				<div css={{ maxWidth: 850, margin: '0 auto' }}>
					<Text component="h2" textAlign="center">
						What can we help you with?
					</Text>
					<Text component="b2" textAlign="center" css={{ marginBottom: 60 }}>
						Our Help Center has answers to frequently asked questions.
					</Text>
				</div>
				<GridWrapper>
					<Disclaimer>
						<Text component="b3">
							Note: <strong>Please do not contact Submittable</strong> support
							to inquire about the status of your submission(s).
						</Text>
						<Text component="b3">
							You must <strong>contact the organization(s)</strong> to which you
							submitted your work directly. Please{' '}
							<Link to="https://submittable.help/en/articles/2576225-how-can-i-communicate-with-an-organization-to-which-i-ve-made-a-submission">
								click here
							</Link>{' '}
							to learn more about how to directly contact organizations to which
							you've submitted.
						</Text>
					</Disclaimer>
					<Grid>
						{helpTopics.map(topic => (
							<GridLink to={topic.link}>{topic.text}</GridLink>
						))}
					</Grid>
				</GridWrapper>
			</Section>

			<Section backgroundColor={colors.grayscale.white}>
				<div>
					<Text component="h2" textAlign="center">
						I need help with something else
					</Text>
					<Text component="b2" textAlign="center" css={{ marginBottom: 60 }}>
						{helpPage.generalMessage}
					</Text>
				</div>
				<div
					css={{
						maxWidth: 700,
						margin: '0 auto',
						paddingBottom: 20,
						textAlign: 'center',
					}}
				>
					<Button link={helpPage.supportLinks.submitter}>
						{helpPage.emailSupport}
					</Button>
				</div>
			</Section>
		</Layout>
	);
};

export default Page;
